import React, { useState, useRef } from "react";
import SafeSiteImage from "./SafeSiteImage";
import SwiperCore, { Navigation, Thumbs } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/swiper.min.css";
import "swiper/components/zoom/zoom.min.css";
import styled, { css } from "styled-components";
import Container from "./Container";
import Section from "./Section";
import CloseIcon from "./Icons/Close";
import { Helmet } from "react-helmet";
import ThreeDIcon from "./Icons/ThreeD";
import ArrowLeftIcon from "./Icons/ArrowLeft";
import ArrowRightIcon from "./Icons/ArrowRight";
import { HtmlContent } from "./Content";

const PropertyGalleryStyles = styled.div`
  ${({ ...props }) => css`
    .swiper-slide {
      height: auto;
    }

    .swiper-container {
      position: relative;
      margin-bottom: 10px;
      margin-left: 1px;
      margin-right: 1px;
      border-radius: 4px;
    }

    .swiper-container-thumbs {
      li {
        cursor: pointer;
      }
      .swiper-slide {
        opacity: 0.8;
      }
      .swiper-slide-thumb-active {
        opacity: 1;
      }
    }
  `}
`;

const GalleryOverlayStyles = styled.div`
  ${({ ...props }) => css`
    display: ${(props.$visible ? "block" : "none")};
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    overflow: auto;
    z-index: 99999;
    background: #fff;
    padding-top: 80px;
    text-align: center;

    button {
      position: fixed;
      top: 32px;
      right: 32px;
      z-index: 999999;
      display: flex;
      align-items: center;
      svg {
        margin-right: 12px;
      }
    }

    img {
      margin: 0 auto 24px auto;
      object-fit: cover
    }
  `}
`;

const GalleryWrapStyles = styled.div`
  ${({ ...props }) => css`
    position: relative;
    cursor: pointer;
    
    .product-main-image {
      height: 100%;
    }

    .product-main-image img {
      width: 100%;
      height: 100%;
      aspect-ratio: 57/40;
      object-fit: cover;
    }
  `}
`;

const ThumbWrapStyles = styled.div`
  ${({ ...props }) => css`
    /* Sort swiper layout shift */
    height: 60px;

    @media (min-width: 370px) {
      height: 70px;
    }

    @media (min-width: 600px) {
      height: 150px;
    }

    @media (min-width: 1400px) {
      height: 100px;
    }

    img {
      border: solid 1px #eee;
      border-radius: 4px;
      overflow: hidden;
      height: 100%;
      width: 100%
    }

    .swiper-slide {
      img {
        aspect-ratio: 172/121;
        object-fit: cover;
      }
      &:hover {
        img {
          border: solid 1px ${props.theme.colors.primary};
        }
      }
    }

    .swiper-container {
      overflow: hidden;

      display: none;

      &.swiper-container-initialized {
        display: block;
      }
    }
  `}
`;

const ThreeDEmbdedStyles = styled.div`
  ${({ ...props }) => css`
    height: calc(100% - 4px);
    * {
      height: calc(100% - 4px);
    }
    iframe {
      height: calc(100% - 4px);
      width: 100%;
      max-width: 1000px;
      margin: 0 auto;
      display: block;
      border-radius: 4px;
      overflow: hidden;
      background: ${props.theme.colors.lightGrey};
    }
  `}
`;

const EmbedThumbStyles = styled.div`
  ${({ ...props }) => css`
    background: ${props.theme.colors.lightGrey};
    border: solid 1px ${props.theme.colors.lightGrey};
    height: 100%;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    overflow: hidden;
    flex-direction: column;

    span {
      font-weight: 900;
      display: block;
      margin-top: 2px;
      text-transform: uppercase;
      font-size: .75rem;
      color: ${props.theme.colors.primary};
    }

    &:hover {
      border: solid 1px ${props.theme.colors.primary};
    }

    @media (min-width: ${props.theme.breakpoints.lg}) {
      span {
        margin-top: 8px;
      }
    }
  `}
`;

SwiperCore.use([Thumbs, Navigation]);

const StyledButton = styled.button`
  ${({ ...props }) => css`
    background: #e8e8e8;
    line-height: 32px;
    padding: 0;
    border-radius: 50%;
    transition: all ease 0.3s;
    height: 32px;
    width: 32px;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: 24px;
    z-index: 9;

    &.next {
      right: 16px;
    }

    &.prev {
      left: 16px;
    }

    &:hover {
      background: #dedede;
    }

    &.swiper-button-disabled {
      opacity: 0.5;
      cursor: default;

      &:hover {
        background: none;
      }
    }
  `}
`;

export const ProductGallery = ({ images, threeDEmbed }) => {
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [galleryOverlayVisibleState, setGalleryOverlayVisibleState] =
    useState(false);

  function handleOpenOverlay() {
    setGalleryOverlayVisibleState(true);
  }

  function handleCloseOverlay() {
    setGalleryOverlayVisibleState(false);
  }

  const prevRef = useRef(null);
  const nextRef = useRef(null);

  return (
    images &&
    images.length > 0 && (
      <PropertyGalleryStyles>
        <Helmet>
          <style>
            {`
              body {
                  overflow: ${galleryOverlayVisibleState ? "hidden" : "auto"}
              }
            `}
          </style>
        </Helmet>
        <GalleryWrapStyles>
          <Swiper
            wrapperTag="ul"
            slidesPerView={1}
            slidesPerGroup={1}
            thumbs={{ swiper: thumbsSwiper }}
            navigation={{
              prevEl: prevRef.current,
              nextEl: nextRef.current,
            }}
          >
            {images.map((image, index) => {
              return (
                <SwiperSlide tag="li" key={image.mediaItemUrl}>
                  <div onClick={handleOpenOverlay} className="product-main-image">
                    <SafeSiteImage image={image.mediaItemUrl} alt={image.altText} width="570" height="400" fit="contain" loading={index === 0 ? 'eager' : 'lazy'} fetchpriority="high" decoding="sync" />
                  </div>
                </SwiperSlide>
              );
            })}

            {threeDEmbed && (
              <SwiperSlide tag="li" key="3dEmbed">
                <ThreeDEmbdedStyles>
                  <HtmlContent
                    htmlString={
                      /* Hack to add lazy loading to iframes - it should really be done with a HTML parser, or manually in WordPress */
                      threeDEmbed.replaceAll('allowfullscreen', 'allowfullscreen loading="lazy"')
                    }
                  />
                </ThreeDEmbdedStyles>
              </SwiperSlide>
            )}

            <StyledButton ref={prevRef} type="button" className="prev">
              <span className="sr-only">Prev</span>
              <ArrowLeftIcon />
            </StyledButton>
            <StyledButton ref={nextRef} type="button" className="next">
              <span className="sr-only">Next</span>
              <ArrowRightIcon />
            </StyledButton>
          </Swiper>
        </GalleryWrapStyles>

        {images.length > 1 && (
          <ThumbWrapStyles>
            <Swiper
              wrapperTag="ul"
              slidesPerView={4}
              slidesPerGroup={1}
              spaceBetween={8}
              loop
              centeredSlides
              centeredSlidesBounds
              onSwiper={setThumbsSwiper}
            >
              {images.map((image) => {
                return (
                  <SwiperSlide tag="li" key={image.mediaItemUrl}>
                    <div>
                      <SafeSiteImage image={image.mediaItemUrl} alt={image.altText} width="172" height="121" fit="contain" loading="eager" fetchpriority="high" decoding="sync" />
                    </div>
                  </SwiperSlide>
                );
              })}
              {threeDEmbed && (
                <SwiperSlide tag="li" key="3dEmbedThmb">
                  <EmbedThumbStyles>
                    {" "}
                    <ThreeDIcon />
                    <span>3D View</span>
                  </EmbedThumbStyles>
                </SwiperSlide>
              )}
            </Swiper>
          </ThumbWrapStyles>
        )}
        <GalleryOverlayStyles $visible={galleryOverlayVisibleState}>
          <Section>
            <Container>
              <button type="button" onClick={handleCloseOverlay}>
                <CloseIcon /> Close
              </button>

              <div>
                {images.map((image) => {
                  return (
                    <div key={image.mediaItemUrl}>
                      <SafeSiteImage image={image.mediaItemUrl} alt={image.altText} width="1204" height="800" />
                    </div>
                  )
                })}
              </div>
            </Container>
          </Section>
        </GalleryOverlayStyles>
      </PropertyGalleryStyles>
    )
  );
};
